import { Button, Divider, EButtonColorVariant, EToastVariant } from '@outdoorsyco/bonfire';
import React, { FC, memo, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import EarningStats from '@/components/referral/EarningStats';
import { IBlockElement } from '@/components/templates/TravelCreditsTemplate';
import { useRouter } from '@/hooks/useRouter';
import { useToast } from '@/hooks/useToast';
import { TRootState } from '@/redux/rootReducer';
import { getEarnedCredits, IEarnedCreditsProps } from '@/services/getEarnedCredits';
import { getCurrencyCode } from '@/utility/currency';
import { getAuthToken } from '@/utility/session';

import css from '../../referral/ReferralStats.module.css';
import RichText from '../wagtail/RichText/RichText';

interface ITravelCreditsHeroProps {
  left: IBlockElement[];
  right: IBlockElement[];
}

const TravelCreditsHero: FC<ITravelCreditsHeroProps> = ({ left = [], right = [] }) => {
  const route = useRouter();
  const intl = useIntl();
  const { showToast } = useToast();
  const isAuthenticated = useSelector<TRootState>(state => state.auth.isAuthenticated);
  const token = getAuthToken();

  const defaultEarnedCredits = {
    available: 0,
    gift_cards_total: 0,
    referrals_total: 0,
  };
  const [earnedCredits, setEarnedCredits] = useState<IEarnedCreditsProps>(defaultEarnedCredits);

  const referralsTitle = intl.formatMessage({
    defaultMessage: 'Referral credits earned',
    id: 'sIZT0c',
  });
  const availableTitle = intl.formatMessage({ defaultMessage: 'Available', id: 'PgJGHT' });

  const { available, referrals_total } = earnedCredits;

  const currency = getCurrencyCode(route.locale);

  const earningCredits = [
    {
      title: referralsTitle,
      credits: referrals_total,
      currency,
    },
    {
      title: availableTitle,
      credits: available,
      currency,
    },
  ];

  useEffect(() => {
    const fetchEarnedCredits = async () => {
      try {
        const credits = await getEarnedCredits();

        if ('available' in credits && 'referrals_total' in credits) {
          const updatedCredits = {
            available: credits.available,
            referrals_total: credits.referrals_total,
          };

          setEarnedCredits(updatedCredits);
        }
      } catch {
        showToast(
          EToastVariant.Error,
          intl.formatMessage({ defaultMessage: 'Oops!', id: 'BEbOqj' }),
          intl.formatMessage({
            defaultMessage: 'Something went wrong, please try again.',
            id: '8GvVWZ',
          }),
        );
      }
    };

    if (isAuthenticated && token) {
      fetchEarnedCredits();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sectionTitle = left.find(lBlock => lBlock.type === 'heading')?.value;
  const richTextCTAs = left.filter(lBlock => lBlock.type === 'richtext');
  const creditStatsTitle = right.find(rBlock => rBlock.type === 'heading')?.value;

  const referalCTALabel = intl.formatMessage({ defaultMessage: 'Refer a friend', id: 'VyKjyg' });

  return (
    <div>
      <section className={`${css.spacing} ${css.sectionGrid}`}>
        <div className="w-full mb-12 max-w-28 lg:mb-0">
          {sectionTitle && <h2 className="mb-6 text-gray-800 h2">{sectionTitle}</h2>}

          {richTextCTAs.map((CTA, idx) => (
            <div
              key={`cta-${idx}`}
              className={`flex flex-col items-start ${idx < richTextCTAs.length - 1 && 'mb-12'} `}>
              <RichText content={CTA.value} />
              <Button
                className="mt-6"
                variant={EButtonColorVariant.Primary}
                label={referalCTALabel}
                href={'/referral/renter'}
              />
            </div>
          ))}
        </div>
        <EarningStats earningCredits={earningCredits} earningsTitle={creditStatsTitle} />
      </section>

      <div className={css.spacing}>
        <Divider />
      </div>
    </div>
  );
};
export default memo(TravelCreditsHero);
